import React, { useState } from 'react'
import { Plus, Edit2, X } from 'lucide-react'
import {
  deleteRestaurantPlatform,
  fetchRestaurantSectionPlatforms,
  FrontendRestaurantSectionPlatform,
} from '../../services/restaurant/restaurantPlatformService'
import { Platform } from '../../types/Platforms';
import { useTranslation } from 'react-i18next'
import { SavePlatformModal, Mode } from './SavePlatformModal'
import { useRestaurantStore } from '../../stores/appRestaurantStore'

interface Props {
  restaurantSectionId: string
}

export const RestaurantPlatformsList: React.FC<Props> = ({ restaurantSectionId }) => {
  const { t } = useTranslation()
  const store = useRestaurantStore()
  const platforms = store.restaurantSectionPlatforms[restaurantSectionId] || []
  const setPlatforms = store.setRestaurantSectionPlatforms

  const [modalOpen, setModalOpen] = useState(false)
  const [mode, setMode] = useState<Mode>('create')
  const [selected, setSelected] = useState<FrontendRestaurantSectionPlatform | null>(null)
  const [deletingPlatform, setDeletingPlatform] = useState<FrontendRestaurantSectionPlatform | null>(null)

  const refresh = async () => {
    const list = await fetchRestaurantSectionPlatforms(restaurantSectionId)
    setPlatforms(restaurantSectionId, list)
  }

  const openCreate = () => {
    setMode('create')
    setSelected(null)
    setModalOpen(true)
  }

  const openEdit = (p: FrontendRestaurantSectionPlatform) => {
    setMode('edit')
    setSelected(p)
    setModalOpen(true)
  }

  const handleDelete = (platform: FrontendRestaurantSectionPlatform) => {
    setDeletingPlatform(platform)
  }

  const confirmDelete = async () => {
    if (!deletingPlatform) return

    await deleteRestaurantPlatform(deletingPlatform.id)
    await refresh()
    setDeletingPlatform(null)
  }

  return (
    <>
      <div className="mb-3">
        <div className="flex items-center gap-2">
          <h4 className="text-sm font-semibold text-zinc-700 dark:text-zinc-300 uppercase tracking-wide">{t('restaurantPlatforms.suppliers')}</h4>
          <button
            onClick={openCreate}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-900 dark:text-zinc-100 text-xs font-medium transition border border-zinc-200 dark:border-zinc-700"
          >
            <Plus className="w-3.5 h-3.5" />
            {t('restaurantPlatforms.add')}
          </button>
        </div>
      </div>

      {platforms.length > 0 ? (
        <ul className="space-y-2">
          {platforms.map(s => (
            <li
              key={s.id}
              className="p-4 rounded-xl flex items-start justify-between bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 hover:border-zinc-300 dark:hover:border-zinc-600 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <p className="font-semibold text-zinc-900 dark:text-zinc-50">
                    {Platform.fromString(s.platformName)}
                  </p>
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      s.enabled
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                    }`}
                  >
                    {s.enabled
                      ? t('restaurantPlatforms.enabled')
                      : t('restaurantPlatforms.disabled')}
                  </span>
                </div>
                <div className="space-y-1 text-sm text-zinc-600 dark:text-zinc-400">
                  <div>
                    <span className="font-medium">{t('restaurantPlatforms.email')}:</span>
                    {s.orderEmails && s.orderEmails.length > 0 ? (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {s.orderEmails.map((email) => (
                          <span
                            key={email}
                            className="inline-block px-2 py-0.5 rounded-md bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 text-xs"
                          >
                            {email}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="ml-1 text-zinc-400 dark:text-zinc-500 italic">None</span>
                    )}
                  </div>
                  <div>
                    <span className="font-medium">{t('restaurantPlatforms.clientName')}:</span>
                    {s.orderClientNames && s.orderClientNames.length > 0 ? (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {s.orderClientNames.map((name) => (
                          <span
                            key={name}
                            className="inline-block px-2 py-0.5 rounded-md bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 text-xs"
                          >
                            {name}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="ml-1 text-zinc-400 dark:text-zinc-500 italic">None</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1 ml-2">
                <button
                  onClick={() => openEdit(s)}
                  className="p-2 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-700 transition"
                  aria-label="Edit platform"
                >
                  <Edit2 className="w-4 h-4 text-zinc-700 dark:text-zinc-300" />
                </button>
                <button
                  onClick={() => handleDelete(s)}
                  className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/30 transition"
                  aria-label="Delete platform"
                >
                  <X className="w-4 h-4 text-red-600 dark:text-red-400" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-zinc-500 dark:text-zinc-400 italic">
          {t('restaurantPlatforms.noSuppliers')}
        </p>
      )}

      <SavePlatformModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        mode={mode}
        restaurantSectionId={restaurantSectionId}
        initial={selected || undefined}
        onSaved={refresh}
      />

      {/* Delete Confirmation Modal */}
      {deletingPlatform && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 dark:bg-black/50" onClick={() => setDeletingPlatform(null)}>
          <div
            className="bg-card dark:bg-card-dark rounded-3xl shadow-2xl w-full max-w-md border border-zinc-200 dark:border-white/20"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 md:p-8">
              <div className="flex items-start justify-between mb-6">
                <h2 className="text-2xl font-bold text-zinc-900 dark:text-zinc-50">
                  {t('restaurantPlatforms.deleteSupplier')}
                </h2>
                <button
                  onClick={() => setDeletingPlatform(null)}
                  className="flex-none p-2 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800 transition"
                  aria-label={t('restaurantPlatforms.close')}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 6 6 18"/><path d="m6 6 12 12"/>
                  </svg>
                </button>
              </div>

              <div className="mb-6">
                <p className="text-zinc-600 dark:text-zinc-400">
                  {t('restaurantPlatforms.deleteSupplierConfirmation', { name: Platform.fromString(deletingPlatform.platformName) })}
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setDeletingPlatform(null)}
                  className="flex-1 px-6 py-3 rounded-2xl text-base font-semibold border border-zinc-200 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition"
                >
                  {t('restaurantPlatforms.cancel')}
                </button>
                <button
                  onClick={confirmDelete}
                  className="flex-1 px-6 py-3 rounded-2xl text-base font-semibold bg-red-600 dark:bg-red-700 text-white dark:text-white hover:bg-red-700 dark:hover:bg-red-800 transition"
                >
                  {t('restaurantPlatforms.deleteSupplierButton')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
